import React from "react";
import { useTranslation } from "react-i18next";
import { useAppDispatch, useAppSelector } from "../../common/hooks/hooks";
import { openMultiPanelOffcanvas } from "../../common/features/multiPanelOffcanvas/multiPanelOffcanvasSlice";
import { toggleSettingsPopup } from "./SettingsPopup/settingsPopupSlice";
import { selectWishlistCount } from "../../templates/ecommerce/wishlist/wishlistSelectors";
import { selectCartCount } from "../../templates/ecommerce/cart/cartSelectors";
import { selectCompareCount } from "../../templates/ecommerce/compare/compareSelectors";
import { useGetCartQuery } from "../../templates/ecommerce/cart/api/cartApi";
import { useGetWishlistQuery } from "../../templates/ecommerce/wishlist/api/wishlistApi";
import { useGetCompareListQuery } from "../../templates/ecommerce/compare/api/compareApi";
import Icon from "../../common/components/Icon/Icon";

const MobileBottomNav: React.FC = () => {
  const { t } = useTranslation();

  useGetCartQuery();
  useGetWishlistQuery();
  useGetCompareListQuery();

  const dispatch = useAppDispatch();
  const itemsCount = useAppSelector(selectCartCount);
  const wishlistCount = useAppSelector(selectWishlistCount);
  const compareCount = useAppSelector(selectCompareCount);

  // --- B2B & Auth Check Variables ---
  const isApprovedMember = window.commonVariables?.isApprovedMember || false;
  const loginUrl = window.commonVariables?.loginUrl || "/wp-login.php";

  // --- Click Handlers with Auth Guards ---
  const handleOpenCart = () => {
    if (!isApprovedMember) {
      window.location.href = loginUrl;
      return;
    }
    dispatch(openMultiPanelOffcanvas("cart"));
  };

  const handleOpenWishlist = () => {
    if (!isApprovedMember) {
      window.location.href = loginUrl;
      return;
    }
    dispatch(openMultiPanelOffcanvas("wishlist"));
  };

  const handleToggleSettings = () => dispatch(toggleSettingsPopup());

  // Compare page is a standard link, restrict its URL if not approved
  const compareUrl = isApprovedMember
    ? window.commonVariables?.compareUrl || "/compare"
    : loginUrl;

  const homeUrl = window.commonVariables?.siteUrl || "/";
  const whatsappNumber = window.commonVariables?.whatsappNumber;

  const handleOpenCategories = () => {
    const wrapper = document.getElementById("mobile-menu-offcanvas-wrapper");
    if (wrapper) {
      wrapper.classList.add("is-open");
      wrapper.setAttribute("aria-hidden", "false");
      document.body.classList.add("mobile-menu-offcanvas-open");

      const catTabBtn = wrapper.querySelector(
        '.mobile-menu-offcanvas__tab-btn[data-target="tab-categories"]',
      ) as HTMLElement;

      if (catTabBtn) {
        catTabBtn.click();
      }
    }
  };

  return (
    <nav className="mobile-bottom-nav">
      {/* Left Group */}
      <div className="mobile-bottom-nav__group">
        <a href={homeUrl} className="mobile-bottom-nav__button">
          <Icon iconName="house" className="icon--stroke-thick" />
          <span className="mobile-bottom-nav__label">
            {t("mobileBottomNav.home", "Home")}
          </span>
        </a>

        {whatsappNumber && (
          <a
            href={`https://wa.me/${whatsappNumber.replace(/\D/g, "")}`}
            className="mobile-bottom-nav__button mobile-bottom-nav__button--whatsapp"
            target="_blank"
            rel="noopener noreferrer"
          >
            <Icon iconName="whatsapp-filled" className="icon--filled" />
            <span className="mobile-bottom-nav__label">
              {t("mobileBottomNav.whatsapp", "WhatsApp")}
            </span>
          </a>
        )}

        <button
          className="mobile-bottom-nav__button"
          onClick={handleOpenCategories}
        >
          <Icon iconName="filter-search" className="icon--stroke-thick" />
          <span className="mobile-bottom-nav__label">
            {t("mobileBottomNav.categories", "Categories")}
          </span>
        </button>
      </div>

      <button
        className="mobile-bottom-nav__button mobile-bottom-nav__button--floating"
        onClick={handleOpenCart}
      >
        <Icon iconName="shopping-cart-filled" className="icon--filled" />
        <span className="mobile-bottom-nav__label">
          {t("mobileBottomNav.cart", "Shopping Cart")}
        </span>
        {itemsCount > 0 && <span className="cart-badge">{itemsCount}</span>}
      </button>

      {/* Right Group */}
      <div className="mobile-bottom-nav__group">
        <button
          className="mobile-bottom-nav__button"
          onClick={handleOpenWishlist}
        >
          <Icon iconName="heart-filled" className="icon--filled" />
          <span className="mobile-bottom-nav__label">
            {t("mobileBottomNav.wishlist", "Wishlist")}
          </span>
          {wishlistCount > 0 && (
            <span className="wishlist-badge">{wishlistCount}</span>
          )}
        </button>

        <a href={compareUrl} className="mobile-bottom-nav__button">
          <Icon iconName="arrows-right-left" className="icon--stroke-thick" />
          <span className="mobile-bottom-nav__label">
            {t("mobileBottomNav.compare", "Compare")}
          </span>
          {compareCount > 0 && (
            <span className="compare-badge">{compareCount}</span>
          )}
        </a>

        <button
          className="mobile-bottom-nav__button"
          onClick={handleToggleSettings}
          data-settings-toggle
        >
          <Icon iconName="cog-filled" className="icon--filled" />
          <span className="mobile-bottom-nav__label">
            {t("mobileBottomNav.settings", "Settings")}
          </span>
        </button>
      </div>
    </nav>
  );
};

export default MobileBottomNav;
